import { Stage } from '../display/Stage';
import { LoaderInfo } from '../display/LoaderInfo';
import type { DisplayObject } from '../display/DisplayObject';
import { Event, KeyboardEvent, MouseEvent } from '../events/Event';
import type { Rectangle } from '../geom/Rectangle';
import type { Platform } from '../platform/types';
import { Runtime, rt, setRuntime } from './runtime';
import { broadcastFrameEvent, constructPass, frameScriptsPass } from './lifecycle';
import { processTimers } from '../utils/Timer';
import { processSoundCompletions } from '../media/Sound';
import { TWIPS, tmatInverse, tmatPoint, toTwips } from '../utils/numeric';

export interface FlashPlayerOptions {
  width?: number;
  height?: number;
  frameRate?: number;
  /** flashVars exposed through stage.loaderInfo.parameters. */
  parameters?: Record<string, string>;
  /** 'immediate' (default): injected input is dispatched at once (between ticks);
   *  'tick': queued and dispatched at the start of the next tick (frame-aligned replays). */
  inputMode?: 'immediate' | 'tick';
  /** Listener / frame-script exception policy (default 'log', like the release Flash Player). */
  errorPolicy?: 'log' | 'throw' | 'silent';
  /** Maximum interval between two presses of a double click (ms of runtime clock). */
  doubleClickTime?: number;
}

/**
 * 'cancel' (PORT, touch): the button is released without a click (a long press that showed a hover hint): MOUSE_UP
 * goes to the object under the pointer as for 'up', but no CLICK / DOUBLE_CLICK follows (as after a release outside).
 */
type MouseInputType = 'move' | 'down' | 'up' | 'cancel' | 'wheel' | 'leave';

type InputItem =
  | { kind: 'mouse'; type: MouseInputType; x: number; y: number; delta: number }
  | { kind: 'key'; type: 'down' | 'up'; keyCode: number; charCode: number; mods: { shift?: boolean; ctrl?: boolean; alt?: boolean } }
  | { kind: 'text'; text: string };

type ClipEv =
  | { t: 'move' }
  | { t: 'rollOut'; to: any }
  | { t: 'rollOver'; from: any }
  | { t: 'press' }
  | { t: 'upInside' }
  | { t: 'release'; index: number }
  | { t: 'releaseOutside' }
  | { t: 'wheel'; delta: number };

/** Lowest common ancestor of two display objects (or null). */
function lowestCommonAncestor(a: any, b: any): any {
  const set = new Set<any>();
  for (let o = a; o; o = o.$parent) set.add(o);
  for (let o = b; o; o = o.$parent) if (set.has(o)) return o;
  return null;
}

/**
 * Owns the Stage and the frame loop. `tick()` runs exactly one AVM2 frame (see lifecycle.ts):
 * clock advance -> timers / SOUND_COMPLETE -> Enter (timelines advance) -> ENTER_FRAME ->
 * Construct -> FRAME_CONSTRUCTED -> frame scripts -> EXIT_FRAME -> (RENDER if invalidated).
 *
 * NOTE on the order: the Flash Player advances playing timelines *before* broadcasting ENTER_FRAME
 * (in an enterFrame handler `currentFrame` already reports the new frame and gotoAndPlay() from
 * a handler is what gets rendered). This follows Ruffle's `run_all_phases_avm2`, which is
 * validated against Flash Player output.
 */
export class FlashPlayer {
  readonly stage: Stage;
  readonly platform: Platform;
  readonly runtime: Runtime;
  readonly loaderInfo: LoaderInfo;
  /** Called after every tick (renderer hook). */
  onAfterTick: ((player: FlashPlayer) => void) | null = null;
  /** Called once per scheduled animation frame after 0..n ticks (renderer hook for start()). */
  onFrame: ((player: FlashPlayer, ticks: number) => void) | null = null;

  /** @internal */ $frame = 0;
  /** @internal */ $mouseTwipsX = 0;
  /** @internal */ $mouseTwipsY = 0;
  /** @internal */ $mouseDown = false;
  /** @internal */ $mouseInStage = true;
  /** @internal */ $hovered: any = null;
  /** @internal */ $pressed: any = null;
  /** @internal */ $clickIndex = 0;
  /** @internal */ $lastPressTime = -Infinity;
  /** @internal */ $lastPressPos: [number, number] = [0, 0];
  /** @internal */ $mods = { shift: false, ctrl: false, alt: false };
  /** @internal */ $drag: { obj: any; lockCenter: boolean; bounds: Rectangle | null; offX: number; offY: number } | null = null;
  /** @internal */ $queue: InputItem[] = [];
  /** @internal */ $inTick = false;
  /** @internal */ $flushing = false;
  private inputMode: 'immediate' | 'tick';
  private doubleClickTime: number;
  private loopCancel: (() => void) | null = null;
  private running = false;

  constructor(platform: Platform, opts: FlashPlayerOptions = {}) {
    const cur = rt();
    const runtime = cur.player ? new Runtime() : cur;
    setRuntime(runtime);
    this.runtime = runtime;
    this.platform = platform;
    runtime.platform = platform;
    runtime.player = this;
    runtime.frameRate = opts.frameRate ?? 50;
    runtime.startTime = platform.now();
    if (opts.errorPolicy) runtime.errorPolicy = opts.errorPolicy;
    this.inputMode = opts.inputMode ?? 'immediate';
    this.doubleClickTime = opts.doubleClickTime ?? 500;
    const w = opts.width ?? 807;
    const h = opts.height ?? 600;
    this.stage = new Stage(w, h, runtime.frameRate);
    runtime.stage = this.stage;
    this.loaderInfo = new LoaderInfo({ parameters: opts.parameters ?? {}, width: w, height: h, frameRate: runtime.frameRate });
    this.stage.$loaderInfo = this.loaderInfo;
  }

  get frame(): number {
    return this.$frame;
  }

  /** Make this player's runtime the current one (only needed when several players coexist). */
  makeCurrent(): void {
    setRuntime(this.runtime);
  }

  /** Add the document root (like the main SWF's document class) to the stage. */
  setRoot(root: DisplayObject): void {
    this.makeCurrent();
    (root as any).$isRootCandidate = true;
    (root as any).$loaderInfo = this.loaderInfo;
    this.loaderInfo.$content = root;
    this.stage.addChildAt(root, 0);
  }

  // ------------------------------------------------------------------ frame loop
  tick(): void {
    this.makeCurrent();
    const r = this.runtime;
    this.$inTick = true;
    try {
      const dt = 1000 / this.stage.$frameRate;
      if (this.platform.advanceClock) this.platform.advanceClock(dt);
      r.virtualTime += dt;
      if (this.inputMode === 'tick') this.flushInput();
      const now = r.now();
      r.phase = 'idle';
      processTimers(now);
      processSoundCompletions(now);
      // Enter: timelines advance.
      r.phase = 'enter';
      this.stage.$enterFrame();
      broadcastFrameEvent('enterFrame');
      // Construct.
      r.phase = 'construct';
      constructPass();
      broadcastFrameEvent('frameConstructed');
      // Frame scripts.
      r.phase = 'scripts';
      frameScriptsPass();
      // Exit.
      r.phase = 'exit';
      broadcastFrameEvent('exitFrame');
      if (this.stage.$invalidated) {
        this.stage.$invalidated = false;
        broadcastFrameEvent('render');
      }
      r.phase = 'idle';
      if (this.$drag) this.updateDrag();
      this.$frame++;
    } finally {
      r.phase = 'idle';
      this.$inTick = false;
    }
    if (this.inputMode === 'immediate' && this.$queue.length) this.flushInput();
    if (this.onAfterTick) this.onAfterTick(this);
  }

  /** Run `n` ticks. */
  run(n: number): void {
    for (let i = 0; i < n; i++) this.tick();
  }

  /** Real-time loop with a fixed-step accumulator (platform.scheduleFrame / rAF / setTimeout). */
  start(): void {
    if (this.running) return;
    this.running = true;
    let last: number | null = null;
    let acc = 0;
    const schedule = (cb: (now: number) => void): (() => void) => {
      if (this.platform.scheduleFrame) return this.platform.scheduleFrame(cb);
      const g: any = globalThis as any;
      if (typeof g.requestAnimationFrame === 'function') {
        const id = g.requestAnimationFrame(cb);
        return () => g.cancelAnimationFrame(id);
      }
      const id = setTimeout(() => cb(Date.now()), 4);
      return () => clearTimeout(id);
    };
    const step = (now: number) => {
      if (!this.running) return;
      if (last === null) last = now;
      acc += Math.min(Math.max(0, now - last), 250);
      last = now;
      const dt = 1000 / this.stage.$frameRate;
      let n = 0;
      while (acc >= dt && n < 5) {
        this.tick();
        acc -= dt;
        n++;
      }
      if (n >= 5) acc = 0;
      if (this.onFrame) this.onFrame(this, n);
      this.loopCancel = schedule(step);
    };
    this.loopCancel = schedule(step);
  }

  stopLoop(): void {
    this.running = false;
    if (this.loopCancel) this.loopCancel();
    this.loopCancel = null;
  }

  // ------------------------------------------------------------------ input
  injectMouse(type: MouseInputType, stageX: number, stageY: number, delta: number = 0): void {
    this.$queue.push({ kind: 'mouse', type, x: +stageX, y: +stageY, delta: +delta || 0 });
    if (this.inputMode === 'immediate' && !this.$inTick) this.flushInput();
  }

  injectKey(type: 'down' | 'up', keyCode: number, charCode: number = 0, mods: { shift?: boolean; ctrl?: boolean; alt?: boolean } = {}): void {
    this.$queue.push({ kind: 'key', type, keyCode: keyCode >>> 0, charCode: charCode >>> 0, mods: { ...mods } });
    if (this.inputMode === 'immediate' && !this.$inTick) this.flushInput();
  }

  /**
   * Insert committed text (paste, IME composition, virtual keyboard) into the focused input
   * TextField: TEXT_INPUT + CHANGE like typing, without KEY_DOWN/KEY_UP (Flash behaviour for
   * pasted/IME text). Newlines are dropped for single-line fields. No-op without a focused input field.
   */
  injectText(text: string): void {
    this.$queue.push({ kind: 'text', text: String(text ?? '') });
    if (this.inputMode === 'immediate' && !this.$inTick) this.flushInput();
  }

  private processText(text: string): void {
    const st = this.stage;
    const f: any = st.$focus && st.$focus.stage === st ? st.$focus : null;
    if (!f || f.$renderKind !== 'text' || f.$type !== 'input') return;
    let s = text.replace(/\r\n|\n/g, '\r');
    if (!f.$multiline) s = s.replace(/\r/g, '');
    if (s) f.$userInsert(s);
  }

  private flushInput(): void {
    if (this.$flushing) return;
    this.$flushing = true;
    this.makeCurrent();
    try {
      while (this.$queue.length) {
        const item = this.$queue.shift() as InputItem;
        if (item.kind === 'mouse') this.processMouse(item.type, item.x, item.y, item.delta);
        else if (item.kind === 'text') this.processText(item.text);
        else this.processKey(item.type, item.keyCode, item.charCode, item.mods);
      }
    } finally {
      this.$flushing = false;
    }
  }

  /** Topmost interactive object under the current mouse position (AVM2 mouse pick). */
  pick(): any {
    const res = this.stage.$mousePick(this.$mouseTwipsX, this.$mouseTwipsY);
    if (res.kind === 'hit' && res.target !== this.stage) return res.target;
    return null;
  }

  private processMouse(type: MouseInputType, x: number, y: number, delta: number): void {
    const r = this.runtime;
    const moved = type === 'move' || toTwips(x) !== this.$mouseTwipsX || toTwips(y) !== this.$mouseTwipsY;
    this.$mouseTwipsX = toTwips(x);
    this.$mouseTwipsY = toTwips(y);
    if (type === 'leave') {
      this.$mouseInStage = false;
    } else this.$mouseInStage = true;
    if (this.$drag) this.updateDrag();
    const events: Array<[any, ClipEv]> = [];
    const newOver = this.$mouseInStage ? this.pick() : null;
    if (moved && type !== 'leave') events.push([newOver ?? this.stage, { t: 'move' }]);
    const curOver = this.$hovered;
    if (newOver !== curOver) {
      if (curOver) events.push([curOver, { t: 'rollOut', to: newOver }]);
      if (newOver) events.push([newOver, { t: 'rollOver', from: curOver }]);
    }
    this.$hovered = newOver;
    if (type === 'down') {
      const now = r.now();
      const [px, py] = this.$lastPressPos;
      if (now - this.$lastPressTime <= this.doubleClickTime && Math.abs(px - x) <= 4 && Math.abs(py - y) <= 4) this.$clickIndex++;
      else this.$clickIndex = 0;
      this.$lastPressTime = now;
      this.$lastPressPos = [x, y];
      this.$mouseDown = true;
      events.push([newOver ?? this.stage, { t: 'press' }]);
      this.$pressed = newOver;
    } else if (type === 'up' || type === 'cancel') {
      this.$mouseDown = false;
      events.push([newOver ?? this.stage, { t: 'upInside' }]);
      const pressed = this.$pressed;
      if (pressed === newOver && type === 'up') events.push([pressed ?? this.stage, { t: 'release', index: this.$clickIndex }]);
      else events.push([pressed ?? this.stage, { t: 'releaseOutside' }]);
      this.$pressed = null;
      if (type === 'cancel') this.$lastPressTime = -Infinity; // not the first half of a double click
    } else if (type === 'wheel') {
      events.push([newOver ?? this.stage, { t: 'wheel', delta }]);
    } else if (type === 'leave') {
      this.stage.$dispatch(new Event(Event.MOUSE_LEAVE, false, false));
    }
    for (const [obj, ev] of events) {
      if (ev.t === 'press') this.updateFocusOnPress(obj);
      this.dispatchClipEvent(obj, ev);
    }
    this.updateButtonStates([curOver, newOver, this.$pressed, ...events.map((e) => e[0])]);
  }

  private updateFocusOnPress(obj: any): void {
    const st = this.stage;
    if (obj && obj.$renderKind === 'text' && (obj.$type === 'input' || obj.$selectable)) st.focus = obj;
    else if (st.$focus && (st.$focus as any).$renderKind === 'text' && st.$focus !== obj) st.focus = null;
  }

  /** SimpleButton visual state from hover/press (Flash "track as button" behaviour). */
  private updateButtonStates(objs: any[]): void {
    const seen = new Set<any>();
    for (const o of objs) {
      if (!o || seen.has(o) || !o.$isButton) continue;
      seen.add(o);
      let s: 'up' | 'over' | 'down' = 'up';
      const hovered = this.$hovered === o;
      const pressed = this.$pressed === o;
      if (o.$enabled && o.$visible) {
        if (hovered && this.$mouseDown && (pressed || o.$trackAsMenu)) s = 'down';
        else if (hovered || (pressed && this.$mouseDown && !o.$trackAsMenu)) s = 'over';
      }
      o.$setButtonState(s);
    }
  }

  private makeMouseEvent(type: string, target: any, bubbles: boolean, related: any, delta = 0): MouseEvent {
    let lx = NaN;
    let ly = NaN;
    if (target && target.$localToGlobalMatrix) {
      const inv = tmatInverse(target.$localToGlobalMatrix());
      if (inv) {
        const [a, b] = tmatPoint(inv, this.$mouseTwipsX, this.$mouseTwipsY);
        lx = a / TWIPS;
        ly = b / TWIPS;
      }
    }
    const e = new MouseEvent(type, bubbles, false, lx, ly, related ?? null, this.$mods.ctrl, this.$mods.alt, this.$mods.shift, this.$mouseDown, delta);
    e.$stageX = this.$mouseTwipsX / TWIPS;
    e.$stageY = this.$mouseTwipsY / TWIPS;
    return e;
  }

  private dispatchClipEvent(obj: any, ev: ClipEv): void {
    // Flash fires no events for a selectable text field that was static text.
    if (obj && obj.$renderKind === 'text' && obj.$selectable && obj.$wasStatic) return;
    switch (ev.t) {
      case 'move':
        obj.$dispatch(this.makeMouseEvent(MouseEvent.MOUSE_MOVE, obj, true, null));
        break;
      case 'press':
        obj.$dispatch(this.makeMouseEvent(MouseEvent.MOUSE_DOWN, obj, true, null));
        break;
      case 'upInside':
        obj.$dispatch(this.makeMouseEvent(MouseEvent.MOUSE_UP, obj, true, null));
        break;
      case 'release': {
        const dbl = ev.index % 2 === 1 && obj.$doubleClickEnabled;
        obj.$dispatch(this.makeMouseEvent(dbl ? MouseEvent.DOUBLE_CLICK : MouseEvent.CLICK, obj, true, null));
        break;
      }
      case 'releaseOutside':
        // MouseEvent.RELEASE_OUTSIDE only exists since Flash Player 11.3: not dispatched (FP 10.1).
        break;
      case 'wheel':
        obj.$dispatch(this.makeMouseEvent(MouseEvent.MOUSE_WHEEL, obj, true, null, ev.delta));
        break;
      case 'rollOut': {
        obj.$dispatch(this.makeMouseEvent(MouseEvent.MOUSE_OUT, obj, true, ev.to));
        const lca = lowestCommonAncestor(obj, ev.to ?? this.stage);
        for (let o = obj; o && o !== lca; o = o.$parent) o.$dispatch(this.makeMouseEvent(MouseEvent.ROLL_OUT, o, false, ev.to));
        break;
      }
      case 'rollOver': {
        const lca = lowestCommonAncestor(obj, ev.from ?? this.stage);
        for (let o = obj; o && o !== lca; o = o.$parent) o.$dispatch(this.makeMouseEvent(MouseEvent.ROLL_OVER, o, false, ev.from));
        obj.$dispatch(this.makeMouseEvent(MouseEvent.MOUSE_OVER, obj, true, ev.from));
        break;
      }
    }
  }

  private processKey(type: 'down' | 'up', keyCode: number, charCode: number, mods: { shift?: boolean; ctrl?: boolean; alt?: boolean }): void {
    this.$mods = { shift: !!mods.shift, ctrl: !!mods.ctrl, alt: !!mods.alt };
    const st = this.stage;
    const focus: any = st.$focus && st.$focus.stage === st ? st.$focus : null;
    const target: any = focus ?? st;
    const ev = new KeyboardEvent(type === 'down' ? KeyboardEvent.KEY_DOWN : KeyboardEvent.KEY_UP, true, false, charCode, keyCode, 0, !!mods.ctrl, !!mods.alt, !!mods.shift);
    target.$dispatch(ev);
    if (type === 'down' && focus && focus.$renderKind === 'text') focus.$handleKey(keyCode, charCode, mods);
  }

  // ------------------------------------------------------------------ cursor / drag
  /**
   * Host cursor for the current hover state (CSS cursor keyword): 'none' when Mouse.hide() is
   * active, 'pointer' over buttons / buttonMode sprites with useHandCursor, 'text' over selectable
   * text fields, otherwise 'default'. Mouse.cursor overrides unless "auto".
   */
  getCursor(): 'none' | 'default' | 'pointer' | 'text' {
    const r = this.runtime;
    if (r.mouseHidden) return 'none';
    switch (r.mouseCursor) {
      case 'arrow':
        return 'default';
      case 'button':
      case 'hand':
        return 'pointer';
      case 'ibeam':
        return 'text';
      default:
        break;
    }
    const o: any = this.$hovered;
    if (!o) return 'default';
    if (o.$isButton) return o.$useHandCursor ? 'pointer' : 'default';
    if (o.$renderKind === 'text' && (o.$selectable || o.$type === 'input')) {
      // A buttonMode ancestor still wins in Flash.
    }
    for (let c = o; c; c = c.$parent) {
      if (c.$buttonMode) {
        if (c.$useHandCursor && (c.$enabled === undefined || c.$enabled)) return 'pointer';
        break;
      }
    }
    if (o.$renderKind === 'text' && (o.$selectable || o.$type === 'input')) return 'text';
    return 'default';
  }

  get mouseHidden(): boolean {
    return this.runtime.mouseHidden;
  }

  /** @internal */ $startDrag(obj: any, lockCenter: boolean, bounds: Rectangle | null): void {
    const parent = obj.$parent;
    let offX = 0;
    let offY = 0;
    if (!lockCenter && parent) {
      const inv = tmatInverse(parent.$localToGlobalMatrix());
      if (inv) {
        const [mx, my] = tmatPoint(inv, this.$mouseTwipsX, this.$mouseTwipsY);
        offX = obj.$m.tx - mx;
        offY = obj.$m.ty - my;
      }
    }
    this.$drag = { obj, lockCenter, bounds, offX, offY };
    this.updateDrag();
  }

  /** @internal */ $stopDrag(obj: any): void {
    if (this.$drag && (!obj || this.$drag.obj === obj)) {
      const o = this.$drag.obj;
      this.$drag = null;
      o.$dropTarget = this.findDropTarget(o);
    }
  }

  private findDropTarget(dragged: any): any {
    const gx = this.$mouseTwipsX;
    const gy = this.$mouseTwipsY;
    const visit = (o: any): any => {
      if (o === dragged || !o.$visible) return null;
      if (o.$isContainer) {
        for (let i = o.$children.length - 1; i >= 0; i--) {
          const hit = visit(o.$children[i]);
          if (hit) return hit;
        }
        return !o.$isStage && o.$hitTestSelf(gx, gy) ? o : null;
      }
      return o.$hitTestShape(gx, gy, 1) ? o : null;
    };
    return visit(this.stage);
  }

  private updateDrag(): void {
    const d = this.$drag;
    if (!d) return;
    const o = d.obj;
    const parent = o.$parent;
    let mx = this.$mouseTwipsX;
    let my = this.$mouseTwipsY;
    if (parent) {
      const inv = tmatInverse(parent.$localToGlobalMatrix());
      if (!inv) return;
      [mx, my] = tmatPoint(inv, mx, my);
    }
    let x = mx + d.offX;
    let y = my + d.offY;
    if (d.bounds) {
      const b = d.bounds;
      const x0 = toTwips(b.x);
      const y0 = toTwips(b.y);
      const x1 = toTwips(b.x + b.width);
      const y1 = toTwips(b.y + b.height);
      x = Math.min(Math.max(x, Math.min(x0, x1)), Math.max(x0, x1));
      y = Math.min(Math.max(y, Math.min(y0, y1)), Math.max(y0, y1));
    }
    o.$m.tx = x | 0;
    o.$m.ty = y | 0;
    o.$transformedByScript = true;
    o.$invalidate();
  }

  /** @internal Display object removed from its parent. */
  $onDetached(_obj: any): void {
    /* hover/press references are kept until the next pick, like Flash (AS3). */
  }
}
